import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { spin } from '../../shared/styles/animations';
import { overrideDesignTokens } from '../../shared/styles/override-design-tokens';
import { ludsBaseFont } from '../../shared/styles/typography';
import '../icon/icon';

type Size = 'small' | 'large';
type Color = 'default' | 'theme';
type AriaLive = 'off' | 'polite' | 'assertive';
type AriaBusy = 'true' | 'false';

@customElement('luds-loader')
export class LudsLoader extends LitElement {
  static override styles = [
    overrideDesignTokens,
    ludsBaseFont,
    spin,
    css`
      :host {
        display: inline-block;
        width: fit-content;
        --luds-loader-default-color: var(--colours-neutrals-gray-700);
        --luds-loader-theme-color: var(--colours-brand-primary);
        --luds-loader-title-colour: var(--semantic-colour-tokens-text-text-primary);
        --luds-loader-description-colour: var(--colours-neutrals-gray-700);
        --luds-loader-text-spacing: var(--spacing-spacing-12);
        --luds-loader-description-spacing: var(--spacing-spacing-04);
        --luds-loader-backdrop-color: var(--surfaces-surface-secondary);
      }

      .loader {
        display: flex;
        flex-direction: column;
        align-items: center;
      }

      .loader-over-backdrop {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
      }

      .icon {
        animation: spin 2s linear infinite;
      }

      .default {
        fill: var(--luds-loader-default-color);
      }

      .theme {
        fill: var(--luds-loader-theme-color);
      }

      .title {
        margin-top: var(--luds-loader-text-spacing);
        font-size: 16px;
        font-style: normal;
        font-weight: 600;
        color: var(--luds-loader-title-colour);
      }

      .description {
        margin-top: var(--luds-loader-description-spacing);
        font-size: 14px;
        text-align: center;
        color: var(--luds-loader-description-colour);
      }

      .overlay {
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1;
      }

      .backdrop {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        opacity: 0.5;
        background: var(--luds-loader-backdrop-color);
      }

      .loader-wrapper {
        position: relative;
        width: 100%;
        height: 100%;
      }
    `,
  ];

  @property({ type: String })
  override title = '';

  @property({ type: String })
  description = '';

  @property({ type: String })
  size: Size = 'large';

  @property({ type: String })
  color: Color = 'default';

  @property({ type: Boolean })
  loading = true;

  @property({ type: Boolean })
  showBackdrop = true;

  @property({ type: Boolean })
  hasInjectedContent = false;

  @property({ type: String })
  override ariaLive: AriaLive = 'polite';

  @property({ type: String })
  override ariaBusy: AriaBusy = 'true';

  override firstUpdated() {
    const slot = this.shadowRoot.querySelector('slot');
    const assignedNodes = slot.assignedNodes();

    if (assignedNodes?.length > 0) {
      this.hasInjectedContent = true;
    } else {
      this.hasInjectedContent = false;
    }
  }

  override render() {
    return html`
      <div class="loader-wrapper">
        <slot></slot>
        ${this.loading
          ? html`
              <div
                class="loader overlay ${this.showBackdrop && this.hasInjectedContent ? 'loader-over-backdrop' : ''}"
                role="status"
                aria-live="${this.ariaLive}"
                aria-busy="${this.ariaBusy}">
                <luds-icon
                  icon="bulk_spinner"
                  class="icon ${this.color}"
                  size="${this.size}"
                  aria-hidden="true"></luds-icon>

                ${this.title ? html`<div class="title">${this.title}</div>` : ''}
                ${this.description ? html`<div class="description">${this.description}</div>` : ''}
              </div>

              <div class=" ${this.showBackdrop && this.hasInjectedContent ? 'backdrop' : ''}"></div>
            `
          : ''}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'luds-loader': LudsLoader;
  }
}
